import gql from 'graphql-tag'

// USED IN: WEB_NOTE
export const GetCompositionById = gql`
  query GetCompositionById($id: ID!) {
    compositions(input: { ids: [$id], includeMetadata: true, includeSections: true }) {
      count
      results {
        # If it's a composition
        ... on Composition {
          id
          userId
          metadata {
            name
            status
            noteTypeId
            appointment {
              id
              startsAt
              reason
              emrEncounterStatus
              emrId
              emrEncounterId
              emrDepartmentId
            }
            patient {
              id
              person {
                firstName
                middleName
                lastName
                dateOfBirth
                gender
                prefix
                suffix
              }
              mrn
            }
            user {
              id
              person {
                firstName
                lastName
                middleName
              }
            }
            submissionInformation {
              destinations
              tryAllDestinations
            }
            submissionStatus {
              warning {
                type
                message
              }
              finalDestination
            }
            clientType
            createdWithS2
          }
          sections {
            id
            name
            status
            content
            diagnosisEntry {
              diagnosis {
                icdCode
                billable
                snomedCode
                description
                snomedDescription
                problemLabel
                hccCode
                panelRanking
                suggestionType                
              }
              detailedDisplay {
                tags {
                  text
                }
              }
              confirmStatus
            }
            pbcSectionFlag
          }
          createdAt
        }
        # If it's a patient note
        ... on PatientNote {
          noteId
          compositionId
          metadata {
            name
            status
            noteTypeId
            appointment {
              id
              startsAt
              reason
            }
            patient {
              id
              person {
                firstName
                middleName
                lastName
                dateOfBirth
                gender
                prefix
                suffix
              }
              mrn
            }
            user {
              id
              person {
                firstName
                lastName
                middleName
              }
            }
            submissionInformation {
              destinations
              tryAllDestinations
            }
            submissionStatus {
              warning {
                type
                message
              }
              finalDestination
            },
            clientType
            createdWithS2
          }
          sections {
            id
            name
            content
            status
            diagnosisEntry {
              diagnosis {
                icdCode
                billable
                snomedCode
                description
                snomedDescription
                problemLabel
                hccCode
                panelRanking
                suggestionType
              }
              detailedDisplay {
                tags {
                  text
                }
              }
              confirmStatus
            }
            navigationKeywords
            status
            cursors {
              actor
              location
            }
            pbcSectionFlag
          }
        }
      }
    }
  }
`

// USED IN: VEIL
export const GetReadOnlyCompositionById = gql`
  query GetReadOnlyCompositionById($id: ID!) {
    compositions(input: { ids: [$id], includeMetadata: true, includeSections: true }) {
      count
      results {
        # If it's a composition
        ... on Composition {
          id
          userId
          metadata {
            name
            status
            noteTypeId
            appointment {
              id
              startsAt
              reason
            }
            patient {
              id
              person {
                firstName
                middleName
                lastName
                dateOfBirth
                gender
                prefix
                suffix
              }
              mrn
            }
            user {
              id
              person {
                firstName
                lastName
                middleName
              }
            }
            submissionInformation {
              destinations
              tryAllDestinations
            }
            submissionStatus {
              warning {
                type
                message
              }
              finalDestination
            },
            clientType
            createdWithS2
          }
          sections {
            id
            name
            status
            content
          }
          createdAt
        }
        # If it's a patient note
        ... on PatientNote {
          noteId
          compositionId
          metadata {
            name
            status
            noteTypeId
            appointment {
              id
              startsAt
              reason
            }
            patient {
              id
              person {
                firstName
                middleName
                lastName
                dateOfBirth
                gender
                prefix
                suffix
              }
              mrn
            }
            user {
              id
              person {
                firstName
                lastName
                middleName
              }
            }
            submissionInformation {
              destinations
              tryAllDestinations
            }
            submissionStatus {
              warning {
                type
                message
              }
              finalDestination
            },
            clientType
            createdWithS2
          }
          sections {
            id
            name
            content
            navigationKeywords
            status
            cursors {
              actor
              location
            }
          }
        }
      }
    }
  }
`

// USED IN: SCHEDULE, PATIENT_NOTES
export const GetCompositionMetadataById = gql`
  query GetCompositionMetadataById($id: ID!) {
    compositions(input: { ids: [$id], includeMetadata: true, includeSections: false }) {
      count
      results {
        ... on Composition {
          id
          metadata {
            name
            noteTypeId
            status
            submissionInformation {
              destinations
              tryAllDestinations
            }
            submissionStatus {
              warning {
                type
                message
              }
              finalDestination
            },
            clientType,
            createdWithS2,
            appointment {
              id
              owner {
                id
                roles {
                  type
                }
                person {
                  firstName
                  middleName
                  lastName
                  prefix
                  suffix
                  gender
                  dateOfBirth
                }
                authId
                email
                specialty
                specialties
                timezone
                userType
                activationSent
              }
              patient {
                id
                person {
                  firstName
                  middleName
                  lastName
                  dateOfBirth
                  gender
                  prefix
                  suffix
                }
                mrn
                emrId
                emrIdType
              }
              startsAt
              endsAt
              type
              medicalProcedure
              reason
              emrId
              emrEncounterId
              emrDepartmentId
            }
            encounterAddress {
              line1
              line2
              city
              state
              zip
              country
            }
            patient {
              id
              person {
                firstName
                middleName
                lastName
                dateOfBirth
                gender
                prefix
                suffix
              }
              mrn
              emrId
              emrIdType
            }
            user {
              id
              roles {
                type
              }
              person {
                firstName
                middleName
                lastName
                prefix
                suffix
                gender
                dateOfBirth
              }
              authId
              email
              specialty
              specialties
              timezone
              userType
              activationSent
            }
          }
        }
        ... on PatientNote {
          noteId
          compositionId
          metadata {
            name
            noteTypeId
            status
            submissionInformation {
              destinations
              tryAllDestinations
            }
            submissionStatus {
              warning {
                type
                message
              }
              finalDestination
            }
            clientType
            createdWithS2
            appointment {
              id
              owner {
                id
                roles {
                  type
                }
                person {
                  firstName
                  middleName
                  lastName
                  prefix
                  suffix
                  gender
                  dateOfBirth
                }
                authId
                email
                specialty
                specialties
                timezone
                userType
                activationSent
              }
              patient {
                id
                person {
                  firstName
                  middleName
                  lastName
                  dateOfBirth
                  gender
                  prefix
                  suffix
                }
                mrn
                emrId
                emrIdType
              }
              startsAt
              endsAt
              type
              medicalProcedure
              reason
              emrId
              emrEncounterId
              emrDepartmentId
            }
            encounterAddress {
              line1
              line2
              city
              state
              zip
              country
            }
            patient {
              id
              person {
                firstName
                middleName
                lastName
                dateOfBirth
                gender
                prefix
                suffix
              }
              mrn
              emrId
              emrIdType
            }
            user {
              id
              roles {
                type
              }
              person {
                firstName
                middleName
                lastName
                prefix
                suffix
                gender
                dateOfBirth
              }
              authId
              email
              specialty
              specialties
              timezone
              userType
              activationSent
            }
          }
        }
      }
    }
  }
`

// USED IN: UNFINISHED_NOTES, SCHEDULE, PATIENT_NOTES, VEIL, ACTION_STREAM
export const GetUnfinishedCompositions = gql`
  query GetUnfinishedCompositions($userId: ID!) {
    incompleteCompositions(input: { userId: $userId, includeMetadata: true, includeSections: false, excludePartialDictation: true }) {
      count
      results {
        id
        sections {
          status
        }
        metadata {
          status
          submissionInformation {
            destinations
            tryAllDestinations
          }
          submissionStatus {
            warning {
              type
              message
            }
            finalDestination
          }
          clientType
          createdWithS2
          appointment {
            id
            startsAt
            reason
          }
          patient {
            id
            person {
              firstName
              middleName
              lastName
              prefix
              suffix
            }
          }
        }
        createdAt
      }
    }
  }
`

// USED IN: HAMBURGER_MENU, PATIENT_NOTES, SCHEDULE, VEIL, ACTION_STREAM
export const GetUnfinishedCompositionsCount = gql`
  query GetUnfinishedCompositionsCount($userId: ID!) {
    incompleteCompositions(input: { userId: $userId, includeMetadata: true, includeSections: false, excludePartialDictation: true }) {
      count
    }
  }
`

// USED IN: WEB_NOTE_SECTION
export const GetCompositionSections = gql`
  query GetCompositionSections($id: ID!, $sectionIds: [ID!]!) {
    compositionSections(input: { id: $id, sectionIds: $sectionIds }) {
      sections {
        id
        name
        content
        navigationKeywords
        status
        cursors {
          actor
          location
        }
        pbcSectionFlag
      }
    }
  }
`

// USED IN: SCHEDULE, PATIENT_NOTES, VEIL, ACTION_STREAM
export const CreateComposition = gql`
  mutation CreateComposition($noteTypeId: ID!, $patientId: ID, $appointmentId: ID) {
    createComposition(input: { noteTypeId: $noteTypeId, patientId: $patientId, appointmentId: $appointmentId }) {
      composition {
        id
        userId
      }
    }
  }
`

// USED IN: SCHEDULE, PATIENT_NOTES, WEB_NOTE_SECTION
export const UpdateComposition = gql`
  mutation UpdateComposition($id: ID!, $metadata: MetadataInput, $sections: [CompositionSectionInput!]) {
    updateComposition(input: { id: $id, metadata: $metadata, sections: $sections }) {
      id
    }
  }
`

export const UpdateCompositionStatus = gql`
  mutation UpdateCompositionStatus($id: ID!, $status: MetadataStatus) {
    updateCompositionStatus(input: { id: $id, status: $status }) {
      id
      metadata {
        status
      }
    }
  }
`

// USED IN: VEIL
export const DeleteCompositions = gql`
  mutation DeleteCompositions($ids: [ID!]) {
    deleteCompositions(input: { ids: $ids }) {
      ids
    }
  }
`

// USED IN: NOTE (Autodelete)
export const AutoDeleteComposition = gql`
  mutation AutoDeleteComposition($organizationId: ID, $userId: ID, $id: ID!) {
    autoDeleteComposition(input: {
      organizationId: $organizationId,
      userId: $userId,
      id: $id
    }) {
      id
    }
  }
`

// USED IN: VEIL
export const SubmitComposition = gql`
  mutation SubmitComposition($id: ID!, $tryAllDestinations: Boolean) {
    submitComposition(input: { id: $id, actor: USER, tryAllDestinations: $tryAllDestinations }) {
      noteId
      submissionStatus {
        warning {
          type
          message
        }
        finalDestination
      }
    }
  }
`

// USED IN: VEIL
export const SubmitCompositionBypassQa = gql`
  mutation SubmitComposition($id: ID!, $tryAllDestinations: Boolean) {
    submitComposition(input: {
      id: $id,
      actor: USER,
      tryAllDestinations: $tryAllDestinations,
      bypassQa: true
    }) {
      noteId
      submissionStatus {
        warning {
          type
          message
        }
        finalDestination
      }
    }
  }
`

// USED IN: NOTE
export const UpdatePatientForComposition = gql`
  mutation UpdatePatientForComposition($id: ID!, $patientId: ID!, $appointmentId: ID) {
    updatePatientForComposition(input: { id: $id, patientId: $patientId, appointmentId: $appointmentId }) {
      metadata {
        name
        noteTypeId
        status
        submissionInformation {
          destinations
          tryAllDestinations
        }
        submissionStatus {
          warning {
            type
            message
          }
          finalDestination
        }
        clientType
        createdWithS2
        appointment {
          id
          owner {
            id
            roles {
              type
            }
            person {
              firstName
              middleName
              lastName
              prefix
              suffix
              gender
              dateOfBirth
            }
            authId
            email
            specialty
            specialties
            timezone
            userType
            activationSent
          }
          patient {
            id
            person {
              firstName
              middleName
              lastName
              dateOfBirth
              gender
              prefix
              suffix
            }
            mrn
            emrId
            emrIdType
          }
          startsAt
          endsAt
          type
          medicalProcedure
          reason
          emrId
          emrEncounterId
          emrDepartmentId
        }
        encounterAddress {
          line1
          line2
          city
          state
          zip
          country
        }
        patient {
          id
          person {
            firstName
            middleName
            lastName
            dateOfBirth
            gender
            prefix
            suffix
          }
          mrn
          emrId
          emrIdType
        }
        user {
          id
          roles {
            type
          }
          person {
            firstName
            middleName
            lastName
            prefix
            suffix
            gender
            dateOfBirth
          }
          authId
          email
          specialty
          specialties
          timezone
          userType
          activationSent
        }
      }
    }
  }
`

// USED IN: VEIL
export const InsertScriptIntoComposition = gql`
  mutation InsertScriptIntoComposition($id: ID!, $scriptId: ID!) {
    insertIntoComposition(input: { id: $id, scriptId: $scriptId }) {
      sections {
        id
        name
        status
        content
      }
    }
  }
`

export const AddCompositionSection = gql`
  mutation AddCompositionSection($compositionId: ID!, $section: CompositionSectionInput!) {
    addCompositionSection(input: { compositionId: $compositionId, section: $section }) {
      section {
        id
        name
        status
        content
        diagnosisEntry {
          diagnosis {
            icdCode
            billable
            snomedCode
            description
            snomedDescription
            problemLabel
            hccCode
            panelRanking
            suggestionType
          }
          confirmStatus
        }
      }
    }
  }
`

export const DeleteCompositionSection = gql`
  mutation DeleteCompositionSection($organizationId: ID!, $compositionId: ID!, $sectionId: ID!) {
    deleteCompositionSection(input: { organizationId: $organizationId, compositionId: $compositionId, sectionId: $sectionId }) {
      success
    }
  }
`

export const LockNoteForEdit = gql`
  mutation LockNoteForEdit ($noteId: ID) {
    lockNoteForEdit(input: {
      noteId: $noteId
    }) {
      scribe {
        id,
        firstName,
        middleName,
        lastName
      }
      noteId,
      status
    }
  }
`

export const GetAmwellNote = gql`
  mutation GetAmwellNote($organizationId: ID, $userId: ID, $appointmentId: ID) {
    getAmwellNote(
      input: {
        organizationId: $organizationId
        userId: $userId
        appointmentId: $appointmentId
      }
    ) {
      compositionId
      patientId
      appointmentId
    }
  }
`
